import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Button, { ButtonProps, ButtonVariant } from '@/components/Button/Button'

export type RadioButtonProps = ButtonProps & {
  current: string
  onChange: (id: string) => void
  id: string
}

export function RadioButton({
  className,
  children,
  disabled,
  current,
  onChange,
  id,
  variant,
  hasParticles,
  appear,
  appearDelay,
}: RadioButtonProps) {
  const realId = useId()
  const _id = id || realId

  return (
    <div className='relative flex flex-col'>
      <Button
        rejected={current !== null && current !== _id}
        ariaCurrent={current === _id ? true : false}
        active={current === _id}
        className={className}
        is='button'
        onClick={() => onChange(_id)}
        disabled={disabled}
        // type={type}
        variant={variant}
        hasParticles={hasParticles}
        appear={appear}
        appearDelay={appearDelay}
      >
        {children}
      </Button>
    </div>
  )
}

export type RadioGroupProps = {
  is?: 'div' | 'nav'
  className?: string
  classNameButtonInner?: string
  choices: Array<{
    value: string
    label: string
    checked?: boolean
  }>
  name: string
  onChange: (choice: string) => void
  variant?: ButtonVariant
  hasParticles?: boolean
  appear?: boolean
  appearDelay?: number
}

export default function RadioGroup({
  is = 'div',
  className,
  choices,
  variant,
  hasParticles,
  appear,
  appearDelay,
  onChange,
}: RadioGroupProps) {
  const { t } = useTranslation()
  const [checked, setChecked] = useState<string | null>(choices.find((choice) => choice.checked)?.value || null)
  const onGroupChange = (id: string) => {
    setChecked(id)
    onChange(id)
  }

  const Component = is

  return (
    <Component className={`${className}`} aria-label={is === 'nav' ? t('summary.nav') : undefined}>
      {choices.map((choice, index) => (
        <RadioButton
          current={checked}
          onChange={onGroupChange}
          key={index}
          id={choice.value}
          variant={variant}
          className='peer-focus-visible:outline outline-scarlet outline-offset-2'
          hasParticles={hasParticles}
          appear={appear}
          appearDelay={appearDelay + index * 0.1}
          ariaCurrent={is === 'nav' ? (checked === choice.value ? true : false) : undefined}
        >
          {choice.label}
        </RadioButton>
      ))}
    </Component>
  )
}
